<template>
  <div class="flex flex-col gap-24 w-full sm:m-0 m-16">
    <div class="flex flex-col gap-8">
      <TypographyH1 v-if="!isMobileScreenSize" class="text-4xl font-bold">
        {{ $t('health_actions.scheduling.approve_scheduling_title') }}
      </TypographyH1>
      <TypographyH2 v-else class="font-semibold">
        {{ $t('health_actions.scheduling.approve_scheduling_title') }}
      </TypographyH2>
      <TypographyBody v-if="selectedDoctors.length > 1">
        {{ $t('health_actions.scheduling.approve_scheduling_description') }}
      </TypographyBody>
    </div>
    <div v-if="selectedDoctors.length > 0" class="flex flex-col gap-24">
      <div class="flex flex-col gap-24">
        <div class="flex flex-col gap-16">
          <div class="flex flex-row justify-between">
            <TypographyBody class="text-lg text-gray-600 font-semibold">
              {{ $t('health_actions.scheduling.preferred_doctor') }}
            </TypographyBody>
            <span
              class="text-blue-500 underline cursor-pointer"
              @click="$emit('edit-doctor')"
              >{{ $t('common.edit') }}</span
            >
          </div>
          <DoctorCard
            v-if="selectedDoctors.length < 2"
            :doctor="selectedDoctors[0]"
          />
          <DoctorCardDropdown
            v-else
            :key="selectedDoctors[0].name"
            :doctor="selectedDoctors[0]"
          />
        </div>
        <div v-if="selectedDoctors.length > 1" class="flex flex-col gap-16">
          <TypographyBody class="text-lg text-gray-600 font-semibold">
            {{
              $t('health_actions.scheduling.approve_scheduling_other_doctors')
            }}
          </TypographyBody>
          <div class="flex flex-col gap-24">
            <DoctorCardDropdown
              v-for="doctor in selectedDoctors.slice(1)"
              :key="doctor.name"
              :doctor="doctor"
              removable
            />
          </div>
        </div>
      </div>
    </div>
    <div v-else class="flex flex-col gap-16">
      <div class="flex flex-row justify-between">
        <TypographyBody class="text-lg text-gray-600 mb-4 font-semibold">
          {{
            $t('health_actions.scheduling.approve_scheduling_your_preferences')
          }}
        </TypographyBody>
        <span
          v-if="getListOfDoctors.length > 0"
          class="text-blue-500 underline cursor-pointer"
          @click="$emit('edit-preferences')"
          >{{ $t('common.edit') }}</span
        >
      </div>
      <div
        v-if="isFetchingProviders"
        class="flex rounded justify-center border border-grey-300 p-24"
      >
        <Icon iconClass="fas fa-spinner fa-pulse fa-6x" />
      </div>
      <div
        v-else-if="getListOfDoctors.length > 0"
        class="flex flex-col rounded border border-grey-300 py-8 px-24"
      >
        <div
          v-for="(entry, index) in Object.entries(getDoctorPreferences)"
          :key="entry[0]"
          :class="[
            'flex flex-row justify-between items-center py-16',
            index !== 0 ? 'border-t border-grey-50' : '',
          ]"
        >
          <div class="flex justify-between w-full">
            <TypographyBody class="text-grey-500">
              {{ $t('health_actions.scheduling.select_doctor_' + entry[0]) }}
            </TypographyBody>
            <TypographyBody>
              {{ getDoctorPreferencesValue(entry[0]) }}
            </TypographyBody>
          </div>
        </div>
      </div>
      <div v-else>
        <NoDoctorsFound
          @open-zip-code-modal="openZipCodeModal"
          @close-zip-code-modal="closeZipCodeModal"
        />
      </div>
    </div>
    <div class="flex flex-col gap-16">
      <div class="flex justify-between">
        <TypographyBody class="text-lg text-gray-600 font-semibold">
          {{ $t('health_actions.scheduling.my_availability') }}
        </TypographyBody>
        <span
          class="text-blue-500 underline cursor-pointer"
          @click="$emit('edit-availability')"
        >
          {{ $t('common.edit') }}</span
        >
      </div>
      <div class="flex flex-col rounded border border-grey-300 p-24">
        <div v-if="!userHasSelectedAvailability">
          <TypographyBody>
            {{
              $t(
                'health_actions.scheduling.approve_scheduling_any_availability'
              )
            }}
          </TypographyBody>
        </div>
        <div v-else class="flex flex-col gap-16">
          <div
            v-for="(timeSlot, index) in getAllSelectedTimeSlots"
            :key="timeSlot.label"
            :class="[
              'flex flex-row justify-between items-center',
              index !== 0 ? 'border-t pt-16 border-grey-50' : '',
            ]"
          >
            <div class="flex flex-col">
              <TypographyBody class="text-grey-500 font-semibold">
                {{ getTimeLabel(timeSlot.label) }}
              </TypographyBody>
              <TypographyBody class="text-grey-500 font-semibold">
                {{ timeSlot.description }}
              </TypographyBody>
            </div>
            <TypographyBody>
              {{ getDaysString(timeSlot.days) }}
            </TypographyBody>
          </div>
        </div>
      </div>
    </div>
    <WarningBox
      :text="$t('health_actions.scheduling.approve_scheduling_disclaimer')"
    />
  </div>
</template>

<script>
import TypographyBody from '~/components/common/Typography/Body.vue'
import TypographyH1 from '~/components/common/Typography/H1.vue'
import TypographyH2 from '~/components/common/Typography/H2.vue'
import DoctorCard from './DoctorCard/DoctorCard.vue'
import WarningBox from './WarningBox.vue'
import DoctorCardDropdown from './DoctorCard/DoctorCardDropdown.vue'
import { mapGetters } from 'vuex'
import { SCHEDULING_AVAILABILITY_TIME_LABELS } from '@platform-shared/constants'
import screenSizeMixin from '~/components/mixins/screenSizeMixin'
import NoDoctorsFound from './NoDoctorsFound.vue'
import doctorPreferencesMixin from './doctorPreferencesMixin'
import Icon from '~/components/common/Icon.vue'

export default {
  components: {
    TypographyH1,
    TypographyBody,
    TypographyH2,
    DoctorCard,
    DoctorCardDropdown,
    WarningBox,
    NoDoctorsFound,
    Icon,
  },
  mixins: [screenSizeMixin, doctorPreferencesMixin],
  computed: {
    ...mapGetters('scheduling', [
      'selectedDoctors',
      'selectedAvailability',
      'userHasSelectedAvailability',
      'getAllSelectedTimeSlots',
      'getDoctorPreferences',
      'getListOfDoctors',
      'isFetchingProviders',
    ]),
  },
  methods: {
    getDaysString(days) {
      const shortDayMap = {
        Monday: 'Mon',
        Tuesday: 'Tues',
        Wednesday: 'Wed',
        Thursday: 'Thurs',
        Friday: 'Fri',
        Saturday: 'Sat',
        Sunday: 'Sun',
      }
      if (days.length > 1) {
        const formattedDays = days.map((day) => shortDayMap[day])
        return formattedDays.join(', ')
      } else {
        return days[0] + 's'
      }
    },
    getTimeLabel(time) {
      return SCHEDULING_AVAILABILITY_TIME_LABELS[time]
    },
    getDoctorPreferencesValue(key) {
      switch (key) {
        case 'distance':
          return this.distanceOptions.find(
            (option) => option.value === this.getDoctorPreferences[key]
          )?.text
        case 'gender':
          return this.genderOptions.find(
            (option) => option.value === this.getDoctorPreferences[key]
          )?.text
      }
      return ''
    },
    openZipCodeModal() {
      this.$emit('open-zip-code-modal')
    },
    closeZipCodeModal() {
      this.$emit('close-zip-code-modal')
    },
  },
}
</script>
